import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { multiStep } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/multiStep'
import { StepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { useMobileContentPlanningContext } from '../ContentPlanningContext/ContentPlanningContext'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { Button } from 'blaze-ui/Button'
import { ArrowRight } from 'blaze-ui/icons'
import { GeneratedContent } from '../ContentPlanningContext/ContentPlanningContextTypes'
import dayjs from 'dayjs'
import Styles from './PostPreviewScreen.module.scss'
import { useRootStore } from 'stores/RootStore'
import { EditCaptionDrawer } from './EditCaptionDrawer'
import { useEffect, useRef } from 'react'
import { LoadingState } from '../GeneratedContent/components/LoadingState'
import { PostPreviewCard } from './PostPreviewCard/PostPreviewCard'

export const PostPreviewScreen = multiStep<StepProps>(({ schema, submit, initialValues }: StepProps) => {
  const { meta } = schema.describe()
  const { brand_kit_id } = initialValues
  const { data } = useBrandKit(brand_kit_id)
  const brandKit = data?.brand_kit
  const {
    generatedContent,
    generateContent,
    isGenerating,
    mediaType,
    updatePostSchedule,
    updatePostCaption,
    updatePostImage,
    updatePostDesign,
    selectedPostId,
    setSelectedPostId,
  } = useMobileContentPlanningContext()
  const { drawersStore } = useRootStore()

  const postRefs = useRef<Record<string, HTMLDivElement>>({})
  const hasTriggeredGeneration = useRef(false)

  useEffect(() => {
    if (generatedContent.length === 0 && !hasTriggeredGeneration.current && !isGenerating) {
      hasTriggeredGeneration.current = true
      generateContent(initialValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedContent.length, isGenerating])

  useEffect(() => {
    if (selectedPostId && postRefs.current[selectedPostId]) {
      const timeoutId = setTimeout(() => {
        postRefs.current[selectedPostId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        })
        setSelectedPostId(undefined)
      }, 150)
      return () => clearTimeout(timeoutId)
    }
  }, [selectedPostId, setSelectedPostId])

  const handleScheduleChange = (uuid: string, newDate: Date | undefined) => {
    if (newDate) {
      updatePostSchedule(uuid, dayjs(newDate))
    }
  }

  const handleCaptionEdit = (content: GeneratedContent) => {
    drawersStore.openDrawer(EditCaptionDrawer, {
      initialText: content.text,
      onSave: (newText: string) => {
        updatePostCaption(content.uuid, newText)
        drawersStore.closeDrawer()
      },
      onCancel: () => {
        drawersStore.closeDrawer()
      },
    })
  }

  const handleImageChange = (uuid: string, newImageUrl: string) => {
    updatePostImage(uuid, newImageUrl)
  }

  const handleDesignChange = (uuid: string, templateId: number) => {
    updatePostDesign(uuid, templateId)
  }

  if (isGenerating || (generatedContent.length === 0 && hasTriggeredGeneration.current)) {
    return <LoadingState mediaType={mediaType} />
  }

  return (
    <>
      <div className={Styles.wrapper}>
        <MobileWizard.Content>
          <MobileWizard.Section>
            <MobileWizard.Title>{meta.title}</MobileWizard.Title>
          </MobileWizard.Section>
        </MobileWizard.Content>

        <div className={Styles.scrollableContent}>
          <div className={Styles.postsList}>
            {generatedContent.map((content) => (
              <div key={content.uuid} ref={(el) => el && (postRefs.current[content.uuid] = el)}>
                <PostPreviewCard
                  content={content}
                  brandKit={brandKit}
                  onScheduleChange={handleScheduleChange}
                  onCaptionEdit={handleCaptionEdit}
                  onImageChange={handleImageChange}
                  onDesignChange={handleDesignChange}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <MobileWizard.ButtonFooter>
        <Button size="lg" variant="primary" endIcon={ArrowRight} fullWidth onPress={submit}>
          Open the Project
        </Button>
      </MobileWizard.ButtonFooter>
    </>
  )
})
